<template>
  <AIVDialog
    v-model="isVisible"
    title="Approve Missed Shift"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal approve-missed-shift-modal"
  >
    <div class="approve-missed-shift-content">
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong>
          {{ shift && shift.date ? formatDate(shift.date) : "Loading..." }}
        </p>
        <p>
          <strong>Scheduled Time:</strong>
          {{ formatTimeRange(
            shift?.scheduledStartTime || shift?.originalData?.scheduled_start || shift?.startTime || shift?.scheduled_start,
            shift?.scheduledEndTime || shift?.originalData?.scheduled_end || shift?.endTime || shift?.scheduled_end,
            shift?.date
          ) }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p v-if="shift?.payer !== 'Non Service' && shift?.originalData?.payer !== 'Non Service'">
          <strong>Client:</strong>
          {{ shift ? formatClient(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Caregiver:</strong>
          {{ shift ? formatCaregiver(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Status:</strong>
          <span
            v-if="shift && (shift.status || shift.shift_status)"
            :class="[
              'status-badge',
              getStatusClass(shift.status || shift.shift_status),
            ]"
            >{{ formatStatus(shift.status || shift.shift_status) }}</span
          >
          <span v-else>Loading...</span>
        </p>
        <p v-if="shift">
          <strong>Service Code:</strong> {{ formatServiceCode(shift) }}
        </p>
      </div>

      <!-- Client Selection (if missing) -->
      <div v-if="isClientMissing" class="mb-3">
        <label class="form-label">Select Client *</label>
        <AIVSelect
          v-model="selectedClientId"
          :options="availableClients"
          placeholder="Select a client"
          :searchable="true"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Caregiver Selection (if missing) -->
      <div v-if="isCaregiverMissing" class="mb-3">
        <label class="form-label">Select Caregiver *</label>
        <AIVSelect
          v-model="selectedCaregiverId"
          :options="availableCaregivers"
          placeholder="Select a caregiver"
          :searchable="true"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Service Code Selection (if multiple) -->
      <div v-if="hasMultipleServiceCodes" class="mb-3">
        <label class="form-label">Select Service Code to Approve *</label>
        <AIVSelect
          v-model="selectedServiceCode"
          :options="serviceCodeOptions"
          placeholder="Select a service code"
          :searchable="false"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Actual Time Entry -->
      <div class="mb-3">
        <label class="form-label">Actual Start Time *</label>
        <AIVTimeSelect
          v-model="actualStartTime"
        />
      </div>

      <div class="mb-3">
        <label class="form-label">Actual End Time *</label>
        <AIVTimeSelect
          v-model="actualEndTime"
        />
      </div>

      <div v-if="totalHours" class="mb-3">
        <p>
          <strong>Total Hours:</strong> {{ totalHours }}
        </p>
      </div>

      <!-- Generic VR activity pickers (LS/SED/SB and any other VR code with an
           admin-managed activity list) -->
      <div
        v-for="code in vrCodesWithActivities"
        :key="'vract-' + code"
        class="mb-3"
      >
        <label class="form-label">
          {{ vrActivityState[code].name }} — Activity Completed
          <template v-if="maxForCode(code) != null">(Select up to {{ maxForCode(code) }})</template>
        </label>
        <AIVSelect
          :value="vrActivityState[code].selectedIds"
          @input="onVrActivitySelect(code, $event)"
          :options="vrActivityState[code].options"
          placeholder="Select activities"
          :multi-select="true"
          :searchable="true"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Legacy Job Development fields (CE/SE/SJBT/TWE): shown only for
           shifts already documented on the jd_* columns, or when the code
           has no configured questions (see showJdFields) -->
      <div v-if="showJdFields">
        <div class="mb-3">
          <label class="form-label">Job Development Activity Completed</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>Places applied to that shift</li>
                <li>Employer follow up completed</li>
                <li>Informational interviews completed</li>
                <li>Resume development progress</li>
                <li>
                  Information about where an interview occurred and preparation
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdActivity"
            type="textarea"
            :rows="4"
            placeholder="Describe job development activities completed..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Outcome of Activity</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  List if you helped the client apply or if they will be
                  applying on their own
                </li>
                <li>Describe how follow up went</li>
                <li>
                  Describe how the interview went and the date they will hear
                  back
                </li>
                <li>
                  Describe if you were able to finish the resume development or
                  if information is still needed
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdOutcome"
            type="textarea"
            :rows="4"
            placeholder="Describe the outcome of activities..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Next Steps Forward</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  What is the plan for your next meeting for job development and
                  are you planning on doing any job development on their behalf
                  before the next meeting?
                </li>
                <li>
                  If there is any follow up with employers or applications that
                  are supposed to be completed by the client, include that
                  information here
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdNextSteps"
            type="textarea"
            :rows="4"
            placeholder="Describe next steps forward..."
          />
        </div>
      </div>

      <!-- JC Service Section -->
      <div v-if="isJCService">
        <div class="mb-3">
          <label class="form-label"
            >Primary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCPrimaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select primary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>

        <div class="mb-3">
          <label class="form-label"
            >Secondary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCSecondaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select secondary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>
      </div>

      <!-- Shift-note questions (any 1-on-1 shift: DSPD, VR, PP) -->
      <div v-if="!isGroupShift && dspdDataLoaded" class="mb-3">
        <div v-if="loadingDSPDData" class="text-center mb-3">
          <i class="fa fa-spinner fa-spin"></i> Loading questions...
        </div>
        <template v-else>
          <!-- Service Code Questions -->
          <div v-for="q in (showJdFields ? [] : visibleDspdQuestions)" :key="'q-'+q.id" class="mb-3">
            <label class="form-label">{{ q.question_text }} <span v-if="q.required" class="text-danger">*</span></label>
            <div
              v-if="q.description"
              class="shift-notes-description mb-2"
              style="white-space: pre-line;"
            >{{ q.description }}</div>
            <div v-if="q.question_type === 'yes_no'" class="yes-no-buttons">
              <el-button
                :type="dspdQuestionAnswers[q.id] === 'yes' ? 'success' : 'default'"
                size="small"
                @click="$set(dspdQuestionAnswers, q.id, 'yes')"
              >Yes</el-button>
              <el-button
                :type="dspdQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
                size="small"
                @click="$set(dspdQuestionAnswers, q.id, 'no')"
              >No</el-button>
            </div>
            <el-select
              v-else-if="q.question_type === 'select'"
              :value="dspdQuestionAnswers[q.id] || ''"
              @input="val => $set(dspdQuestionAnswers, q.id, val)"
              placeholder="Select an option"
              style="width: 100%;"
            >
              <el-option
                v-for="(opt, i) in (q.options || [])"
                :key="i"
                :label="opt"
                :value="opt"
              />
            </el-select>
            <el-select
              v-else-if="q.question_type === 'multi_select'"
              multiple
              :value="parseMultiAnswer(dspdQuestionAnswers[q.id])"
              @input="val => $set(dspdQuestionAnswers, q.id, joinMultiAnswer(val))"
              placeholder="Select all that apply"
              style="width: 100%;"
            >
              <el-option
                v-for="(opt, i) in (q.options || [])"
                :key="i"
                :label="opt"
                :value="opt"
              />
            </el-select>
            <el-input
              v-else
              :value="dspdQuestionAnswers[q.id] || ''"
              @input="val => $set(dspdQuestionAnswers, q.id, val)"
              type="textarea"
              :rows="3"
              :placeholder="q.question_text"
            />
          </div>

          <!-- Client Goal Questions (DSPD 1-on-1) -->
          <div v-if="isDSPDOneOnOneShift && dspdClientGoals.length > 0">
            <label class="form-label" style="font-weight: 700;">Client Goals</label>
            <div v-for="goal in dspdClientGoals" :key="'g-'+goal.id" class="mb-3">
              <label class="form-label">{{ goal.question_text }}</label>
              <div v-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
                <el-button
                  :type="dspdGoalAnswers[goal.id] === 'yes' ? 'success' : 'default'"
                  size="small"
                  @click="$set(dspdGoalAnswers, goal.id, 'yes')"
                >Yes</el-button>
                <el-button
                  :type="dspdGoalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
                  size="small"
                  @click="$set(dspdGoalAnswers, goal.id, 'no')"
                >No</el-button>
              </div>
              <el-input
                v-else
                :value="dspdGoalAnswers[goal.id] || ''"
                @input="val => $set(dspdGoalAnswers, goal.id, val)"
                type="textarea"
                :rows="3"
                :placeholder="goal.question_text"
              />
            </div>
          </div>

          <!-- Concerns (DSPD 1-on-1; legacy shifts and question-less codes only) -->
          <div v-if="showConcernsField" class="mb-3">
            <label class="form-label">Do you have any concerns about this individual today?</label>
            <el-input
              v-model="dspdConcerns"
              type="textarea"
              :rows="3"
              placeholder="Enter any concerns..."
            />
          </div>
        </template>
      </div>

      <div class="mb-3" v-if="!isJDService && !isDSPDOneOnOneShift && !hasShiftNoteQuestions">
        <label class="form-label">Shift Notes</label>
        <div
          v-if="isLSService || isSEDService"
          class="shift-notes-description mb-2"
        >
          <div>
            Please include:
            <ul>
              <li>How your client responded to the lesson</li>
              <li>Issues experienced during the lesson</li>
              <li>Plan for next life skills meeting</li>
              <li>Successful outcomes from the lessons</li>
            </ul>
          </div>
        </div>
        <div v-if="isSBService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Update on progress of forms being submitted or response from
                follow up
              </li>
              <li>Any documentation that is still needing to be submitted</li>
            </ul>
          </div>
        </div>
        <div v-if="isJCService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Challenges faced or issues holding the client back from
                independence (Most Important)
              </li>
              <li>
                What are we doing to overcome those challenges? Each challenge
                should have a way or idea to implement to overcome it
              </li>
              <li>
                A run down of how the shift went (like a play by play but
                doesn't need to be as specific)
              </li>
              <li>Important work updates</li>
              <li>
                Client successes and where they are doing well and don't need
                support (This will help determine where support can be faded)
              </li>
            </ul>
          </div>
        </div>
        <el-input
          v-model="shiftNotes"
          type="textarea"
          :rows="vrCodesWithActivities.length || isJCService ? 6 : 3"
          placeholder="Shift Notes"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button type="success" @click="handleApprove" :loading="approving">
          <i class="fa-solid fa-check"></i> Approve Shift
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { localDateString } from "@/utils/dateOnly";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { jobCoachingActivitiesService } from "src/serviceHandlers/jobCoachingActivitiesService";
import vrActivitiesMixin from "src/mixins/vrActivitiesMixin";
import { clientsService } from "@/serviceHandlers/clientsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "ApproveMissedShift",
  components: { AIVDialog, AIVSelect, AIVTimeSelect },
  mixins: [vrActivitiesMixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      approving: false,
      selectedServiceCode: null,
      selectedClientId: null,
      selectedCaregiverId: null,
      availableClients: [],
      availableCaregivers: [],
      loadingClients: false,
      loadingCaregivers: false,
      actualStartTime: "",
      actualEndTime: "",
      shiftNotes: "",
      selectedActivities: [],
      selectedSEDActivities: [],
      selectedSBActivities: [],
      jdActivity: "",
      jdOutcome: "",
      jdNextSteps: "",
      selectedJCPrimaryActivities: [],
      selectedJCSecondaryActivities: [],
      jobCoachingActivities: [],
      loadingJobCoachingActivities: false,
      // DSPD 1-on-1 Q&A data
      dspdServiceCodeQuestions: [],
      dspdClientGoals: [],
      dspdQuestionAnswers: {},
      dspdGoalAnswers: {},
      dspdConcerns: "",
      dspdDataLoaded: false,
      loadingDSPDData: false,
      // LS / SED / SB activities are admin-managed in Settings and loaded from
      // the vr_activities table (see fetchVRActivities).
      lsActivities: [],
      sedActivities: [],
      sbActivities: [],
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    serviceCodeOptions() {
      if (!this.shift) {
        return [];
      }

      // Check for non-service codes first (for Non Service shifts)
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.map((sc) => ({
          id: sc.code,
          name: sc.code,
        }));
      }

      // Check for service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.map((sc) => ({
          id: sc.code,
          name: sc.code,
        }));
      }

      // Check for authorization.service_code (single service code)
      if (this.shift.authorization && this.shift.authorization.service_code && this.shift.authorization.service_code.code) {
        return [{
          id: this.shift.authorization.service_code.code,
          name: this.shift.authorization.service_code.code,
        }];
      }

      // Check originalData for authorization.service_code
      if (this.shift.originalData?.authorization && this.shift.originalData.authorization.service_code && this.shift.originalData.authorization.service_code.code) {
        return [{
          id: this.shift.originalData.authorization.service_code.code,
          name: this.shift.originalData.authorization.service_code.code,
        }];
      }

      return [];
    },

    hasMultipleServiceCodes() {
      return this.serviceCodeOptions.length > 1;
    },

    totalHours() {
      if (!this.actualStartTime || !this.actualEndTime) return null;

      try {
        // Get the shift date from scheduled_start to ensure we use the correct date
        let shiftDate;
        const scheduledStart = this.shift?.scheduledStartTime || this.shift?.originalData?.scheduled_start || this.shift?.startTime || this.shift?.scheduled_start;

        if (scheduledStart && scheduledStart.includes('T')) {
          // Parse the UTC scheduled start time and convert to Mountain Time to get the correct date
          const utcDate = new Date(scheduledStart);
          const mountainTimeDate = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/Denver" }));
          const year = mountainTimeDate.getFullYear();
          const month = String(mountainTimeDate.getMonth() + 1).padStart(2, '0');
          const day = String(mountainTimeDate.getDate()).padStart(2, '0');
          shiftDate = `${year}-${month}-${day}`;
        } else {
          // Fallback to shift.date if scheduled_start is not available
          shiftDate = this.shift?.date ? this.shift.date.split("T")[0] : localDateString(); // local today, not UTC
        }

        // Parse time strings (format: "HH:mm")
        const [startHours, startMinutes] = this.actualStartTime.split(":").map(Number);
        const [endHours, endMinutes] = this.actualEndTime.split(":").map(Number);

        // Parse date components and create Date objects in local timezone
        const [year, month, day] = shiftDate.split('-').map(Number);
        const startDateTime = new Date(year, month - 1, day, startHours, startMinutes, 0, 0);
        const endDateTime = new Date(year, month - 1, day, endHours, endMinutes, 0, 0);

        let diffMs = endDateTime.getTime() - startDateTime.getTime();

        // Handle overnight shifts
        if (diffMs < 0) {
          const nextDayEndTime = new Date(endDateTime);
          nextDayEndTime.setDate(nextDayEndTime.getDate() + 1);
          diffMs = nextDayEndTime.getTime() - startDateTime.getTime();
        }

        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        if (hours === 0) {
          return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
        } else if (minutes === 0) {
          return `${hours} hour${hours !== 1 ? "s" : ""}`;
        } else {
          return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
        }
      } catch (error) {
        return null;
      }
    },

    isLSService() {
      if (!this.shift) return false;

      // If multiple service codes, check the selected one
      if (this.hasMultipleServiceCodes) {
        return this.selectedServiceCode === "LS";
      }

      // Check non-service codes first
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.some((sc) => sc.code === "LS");
      }

      // Check service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "LS");
      }

      // Check authorization.service_code
      const authServiceCode = this.shift.authorization?.service_code?.code || this.shift.originalData?.authorization?.service_code?.code;
      if (authServiceCode) {
        return authServiceCode === "LS";
      }

      return false;
    },

    isSEDService() {
      if (!this.shift) return false;

      // If multiple service codes, check the selected one
      if (this.hasMultipleServiceCodes) {
        return this.selectedServiceCode === "SED";
      }

      // Check non-service codes first
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.some((sc) => sc.code === "SED");
      }

      // Check service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SED");
      }

      // Check authorization.service_code
      const authServiceCode = this.shift.authorization?.service_code?.code || this.shift.originalData?.authorization?.service_code?.code;
      if (authServiceCode) {
        return authServiceCode === "SED";
      }

      return false;
    },

    isSBService() {
      if (!this.shift) return false;

      // If multiple service codes, check the selected one
      if (this.hasMultipleServiceCodes) {
        return this.selectedServiceCode === "SB";
      }

      // Check non-service codes first
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.some((sc) => sc.code === "SB");
      }

      // Check service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SB");
      }

      // Check authorization.service_code
      const authServiceCode = this.shift.authorization?.service_code?.code || this.shift.originalData?.authorization?.service_code?.code;
      if (authServiceCode) {
        return authServiceCode === "SB";
      }

      return false;
    },

    // Legacy JD documentation already stored on the shift's jd_* columns
    // (from before the JD fields became configurable shift_note_questions)
    hasLegacyJdData() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!(
        s.jd_activity || s.jd_outcome || s.jd_next_steps ||
        o.jd_activity || o.jd_outcome || o.jd_next_steps
      );
    },

    // Show the bespoke jd_* fields only for shifts already documented that
    // way, or as a fallback when the JD code has no configured questions.
    showJdFields() {
      return (
        this.isJDService &&
        (this.hasLegacyJdData || this.dspdServiceCodeQuestions.length === 0)
      );
    },

    // Legacy DSPD 1-on-1 concerns already stored as shifts.shift_notes
    hasLegacyConcerns() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!((s.shift_notes || o.shift_notes || "").toString().trim());
    },

    // Hardcoded concerns textarea: legacy shifts and question-less codes only
    showConcernsField() {
      return (
        this.isDSPDOneOnOneShift &&
        (this.hasLegacyConcerns || this.dspdServiceCodeQuestions.length === 0)
      );
    },

    // Rendered question list — hides the seeded concerns question while the
    // legacy concerns textarea is active.
    visibleDspdQuestions() {
      if (!this.showConcernsField) return this.dspdServiceCodeQuestions;
      return this.dspdServiceCodeQuestions.filter(
        (q) => q.question_key !== "dspd_concerns",
      );
    },

    isJDService() {
      if (!this.shift) return false;

      // If multiple service codes, check the selected one
      if (this.hasMultipleServiceCodes) {
        return ["CE", "SE", "SJBT", "TWE"].includes(this.selectedServiceCode);
      }

      // Check non-service codes first
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.some((sc) =>
          sc.code === "CE" || sc.code === "SE" || sc.code === "SJBT" || sc.code === "TWE"
        );
      }

      // Check service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) =>
          sc.code === "CE" || sc.code === "SE" || sc.code === "SJBT" || sc.code === "TWE"
        );
      }

      // Check authorization.service_code
      const authServiceCode = this.shift.authorization?.service_code?.code || this.shift.originalData?.authorization?.service_code?.code;
      if (authServiceCode) {
        return ["CE", "SE", "SJBT", "TWE"].includes(authServiceCode);
      }

      return false;
    },

    isJCService() {
      if (!this.shift) return false;

      // If multiple service codes, check the selected one
      if (this.hasMultipleServiceCodes) {
        return this.selectedServiceCode === "JC";
      }

      // Check non-service codes first
      const nonServiceCodes = this.shift.non_service_codes || this.shift.nonServiceCodes || this.shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        return nonServiceCodes.some((sc) => sc.code === "JC");
      }

      // Check service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "JC");
      }

      // Check authorization.service_code
      const authServiceCode = this.shift.authorization?.service_code?.code || this.shift.originalData?.authorization?.service_code?.code;
      if (authServiceCode) {
        return authServiceCode === "JC";
      }

      return false;
    },

    isDSPDOneOnOneShift() {
      if (!this.shift) return false;
      const payer = this.shift.payer || this.shift.originalData?.payer;
      if (payer !== "DSPD") return false;
      // Not a group shift
      const shiftType = this.shift.shift_type || this.shift.originalData?.shift_type;
      return shiftType !== "Group";
    },

    isGroupShift() {
      if (!this.shift) return false;
      const shiftType = this.shift.shift_type || this.shift.originalData?.shift_type;
      return shiftType === "Group" || this.shift.shiftType === "Group";
    },

    // True when admin-configured shift-note questions apply to this shift —
    // they replace the freeform "Shift Notes" box (parity with clock-out).
    hasShiftNoteQuestions() {
      return !this.isGroupShift && this.dspdServiceCodeQuestions.length > 0;
    },

    isClientMissing() {
      if (!this.shift) return false;
      // Non Service shifts don't require a client
      if (this.shift.payer === 'Non Service' || this.shift.originalData?.payer === 'Non Service') {
        return false;
      }
      const clientId = this.shift.originalData?.clients?.[0]?.id || this.shift.clients?.[0]?.id;
      return !clientId;
    },

    isCaregiverMissing() {
      if (!this.shift) return false;
      const caregiverId = this.shift.originalData?.caregivers?.[0]?.id || this.shift.caregivers?.[0]?.id;
      return !caregiverId;
    },
  },

  watch: {
    async visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.loadClientsIfNeeded();
        this.loadCaregiversIfNeeded();
        await this.loadVrActivities();
        this.loadSelectedVrActivities(this.shift);
        if (!this.isGroupShift) {
          this.loadDSPDOneOnOneData();
        }
      }
    },

    // Reload activity options when the user picks a code on a multi-code shift.
    async selectedServiceCode() {
      await this.loadVrActivities();
      this.loadSelectedVrActivities(this.shift);
    },

    selectedJCPrimaryActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedJCPrimaryActivities = newVal.slice(0, 3);
      }
    },

    selectedJCSecondaryActivities(newVal) {
      if (newVal && newVal.length > 3) {
        this.selectedJCSecondaryActivities = newVal.slice(0, 3);
      }
    },

    isJCService(newVal) {
      if (newVal) {
        this.fetchJobCoachingActivities();
      }
    },

    jobCoachingActivities(newVal) {
      // Once job coaching activities are loaded, populate pending activities
      if (newVal && newVal.length > 0) {
        if (this._pendingJCPrimaryActivities) {
          this.selectedJCPrimaryActivities = this._pendingJCPrimaryActivities
            .map(activityName => {
              const found = this.jobCoachingActivities.find(act => act.name === activityName);
              return found ? found.id : null;
            })
            .filter(id => id !== null);
          this._pendingJCPrimaryActivities = null;
        }

        if (this._pendingJCSecondaryActivities) {
          this.selectedJCSecondaryActivities = this._pendingJCSecondaryActivities
            .map(activityName => {
              const found = this.jobCoachingActivities.find(act => act.name === activityName);
              return found ? found.id : null;
            })
            .filter(id => id !== null);
          this._pendingJCSecondaryActivities = null;
        }
      }
    },
  },

  methods: {
    // multi_select answers are stored as a comma-joined string so every
    // read/display/report path renders them as-is; convert to/from the array
    // the multiple-select control needs.
    parseMultiAnswer(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === "") return [];
      return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    joinMultiAnswer(value) {
      return (Array.isArray(value) ? value : []).join(", ");
    },

    resetForm() {
      this.selectedClientId = null;
      this.selectedCaregiverId = null;

      // Reset DSPD Q&A data
      this.dspdServiceCodeQuestions = [];
      this.dspdClientGoals = [];
      this.dspdQuestionAnswers = {};
      this.dspdGoalAnswers = {};
      this.dspdConcerns = "";
      this.dspdDataLoaded = false;

      // Pre-populate actual times with scheduled times
      this.actualStartTime = this.extractTimeFromScheduled('start');
      this.actualEndTime = this.extractTimeFromScheduled('end');

      // Pre-populate notes and activities if they exist from the caregiver
      this.populateExistingNotesAndActivities();

      this.initializeSelectedServiceCode();
    },

    extractTimeFromScheduled(type) {
      if (!this.shift) return "";

      const timeField = type === 'start'
        ? (this.shift.scheduledStartTime || this.shift.originalData?.scheduled_start || this.shift.startTime || this.shift.scheduled_start)
        : (this.shift.scheduledEndTime || this.shift.originalData?.scheduled_end || this.shift.endTime || this.shift.scheduled_end);

      if (!timeField) return "";

      try {
        // If it's an ISO string with timezone
        if (timeField.includes('T')) {
          const utcDate = new Date(timeField);
          const mountainTime = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/Denver" }));
          const hours = String(mountainTime.getHours()).padStart(2, '0');
          const minutes = String(mountainTime.getMinutes()).padStart(2, '0');
          return `${hours}:${minutes}`;
        }

        // If it's already in HH:mm format
        if (timeField.match(/^\d{2}:\d{2}/)) {
          return timeField.substring(0, 5);
        }
      } catch (error) {
        // Silent error handling
      }

      return "";
    },

    populateExistingNotesAndActivities() {
      // Reset to defaults first
      this.shiftNotes = "";
      this.selectedActivities = [];
      this.selectedSEDActivities = [];
      this.selectedSBActivities = [];
      this.jdActivity = "";
      this.jdOutcome = "";
      this.jdNextSteps = "";
      this.selectedJCPrimaryActivities = [];
      this.selectedJCSecondaryActivities = [];

      if (!this.shift) return;

      // Check for existing notes on the shift
      const existingNotes = this.shift.notes || this.shift.originalData?.notes || this.shift.shift_notes;
      if (existingNotes) {
        this.shiftNotes = existingNotes;
      }

      // LS/SED/SB and other generic VR activities are loaded via the mixin
      // (loadVrActivities + loadSelectedVrActivities) in the visible watcher.

      // Check for existing JD (Job Development) fields
      const jdActivity = this.shift.jd_activity || this.shift.originalData?.jd_activity;
      const jdOutcome = this.shift.jd_outcome || this.shift.originalData?.jd_outcome;
      const jdNextSteps = this.shift.jd_next_steps || this.shift.originalData?.jd_next_steps;

      if (jdActivity) this.jdActivity = jdActivity;
      if (jdOutcome) this.jdOutcome = jdOutcome;
      if (jdNextSteps) this.jdNextSteps = jdNextSteps;

      // Check for existing JC (Job Coaching) primary activities
      const jcPrimaryActivities = this.shift.jc_primary_activities || this.shift.originalData?.jc_primary_activities;
      if (jcPrimaryActivities && Array.isArray(jcPrimaryActivities)) {
        // We need to wait for job coaching activities to load first
        // This will be handled in the watch for jobCoachingActivities
        this._pendingJCPrimaryActivities = jcPrimaryActivities;
      }

      // Check for existing JC secondary activities
      const jcSecondaryActivities = this.shift.jc_secondary_activities || this.shift.originalData?.jc_secondary_activities;
      if (jcSecondaryActivities && Array.isArray(jcSecondaryActivities)) {
        this._pendingJCSecondaryActivities = jcSecondaryActivities;
      }
    },

    initializeSelectedServiceCode() {
      // If only one service code, auto-select it
      if (this.serviceCodeOptions.length === 1) {
        this.selectedServiceCode = this.serviceCodeOptions[0].id;
      } else {
        // If multiple, reset to null to force selection
        this.selectedServiceCode = null;
      }
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    // vrActivities mixin hook. On a multi-code shift the user picks one code
    // via selectedServiceCode; otherwise derive from the shift's codes/auth.
    getShiftVrCodes() {
      if (this.hasMultipleServiceCodes) {
        return this.selectedServiceCode ? [this.selectedServiceCode] : [];
      }
      const fromShift = (
        this.shift?.service_codes ||
        this.shift?.originalData?.service_codes ||
        []
      ).map((sc) => sc.code);
      if (fromShift.length) return fromShift;
      const authCode =
        this.shift?.authorization?.service_code?.code ||
        this.shift?.originalData?.authorization?.service_code?.code;
      return authCode ? [authCode] : [];
    },

    async fetchJobCoachingActivities() {
      if (this.jobCoachingActivities.length > 0) {
        return;
      }

      try {
        this.loadingJobCoachingActivities = true;
        const response = await jobCoachingActivitiesService.list();
        this.jobCoachingActivities = response || [];
      } catch (error) {
        this.$notifyError("Failed to load job coaching activities");
      } finally {
        this.loadingJobCoachingActivities = false;
      }
    },

    async loadClientsIfNeeded() {
      if (!this.isClientMissing) return;

      try {
        this.loadingClients = true;
        const response = await clientsService.list({ slim: true });
        this.availableClients = (response || []).map((client) => ({
          id: Number(client.id) || client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));
      } catch (error) {
        this.$notifyError("Failed to load clients");
      } finally {
        this.loadingClients = false;
      }
    },

    async loadCaregiversIfNeeded() {
      if (!this.isCaregiverMissing) return;

      try {
        this.loadingCaregivers = true;
        const response = await caregiversService.list();
        this.availableCaregivers = (response || []).map((caregiver) => ({
          id: Number(caregiver.id) || caregiver.id,
          name: `${caregiver.first_name} ${caregiver.last_name}`,
        }));
      } catch (error) {
        this.$notifyError("Failed to load caregivers");
      } finally {
        this.loadingCaregivers = false;
      }
    },

    async loadDSPDOneOnOneData() {
      this.loadingDSPDData = true;
      try {
        // Fetch service code questions
        const serviceCodes = this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
        if (serviceCodes.length > 0) {
          const questionPromises = serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          );
          const questionResults = await Promise.all(questionPromises);
          const allQuestions = [];
          const seenIds = new Set();
          for (const questions of questionResults) {
            for (const q of (questions || [])) {
              if (!seenIds.has(q.id)) {
                seenIds.add(q.id);
                allQuestions.push(q);
              }
            }
          }
          this.dspdServiceCodeQuestions = allQuestions.sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
          );
        }

        // Fetch client goals (DSPD 1-on-1 only)
        const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
        if (this.isDSPDOneOnOneShift && clients.length > 0) {
          const clientId = clients[0].id;
          if (clientId) {
            try {
              const response = await clientsService.getGoals(clientId);
              this.dspdClientGoals = (response.goals || []).filter(g => g.status === "active");
            } catch {
              this.dspdClientGoals = [];
            }
          }
        }

        // Load existing question answers from shift data (if caregiver already filled some)
        const allAnswers = this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
        for (const answer of allAnswers) {
          let ans = answer.answer || "";
          if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
            ans = ans.toLowerCase();
          }
          this.$set(this.dspdQuestionAnswers, answer.question_id, ans);
        }

        // Load existing goal answers
        const allGoalAnswers = this.shift?.goalAnswers || this.shift?.originalData?.goalAnswers || [];
        for (const answer of allGoalAnswers) {
          let ans = answer.answer || "";
          if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
            ans = ans.toLowerCase();
          }
          this.$set(this.dspdGoalAnswers, answer.id_goals, ans);
        }

        // Load existing concerns
        this.dspdConcerns = this.shift?.shift_notes || this.shift?.originalData?.shift_notes || "";

        this.dspdDataLoaded = true;
      } catch (error) {
      } finally {
        this.loadingDSPDData = false;
      }
    },

    async handleApprove() {
      // Validate client selection
      if (this.isClientMissing && !this.selectedClientId) {
        this.$notifyError("Please select a client");
        return;
      }

      // Validate caregiver selection
      if (this.isCaregiverMissing && !this.selectedCaregiverId) {
        this.$notifyError("Please select a caregiver");
        return;
      }

      // Validate service code selection (if multiple)
      if (this.hasMultipleServiceCodes && !this.selectedServiceCode) {
        this.$notifyError("Please select a service code to approve with");
        return;
      }

      // Validate times
      if (!this.actualStartTime) {
        this.$notifyError("Actual start time is required");
        return;
      }

      if (!this.actualEndTime) {
        this.$notifyError("Actual end time is required");
        return;
      }

      // Validate required shift-note questions (any 1-on-1 shift; skipped
      // when the legacy JD fields carry the documentation instead)
      if (!this.isGroupShift && this.dspdDataLoaded && !this.showJdFields) {
        for (const q of this.visibleDspdQuestions) {
          if (q.required && !this.dspdQuestionAnswers[q.id]?.trim()) {
            this.$notifyError(`Please answer: "${q.question_text}"`);
            return;
          }
        }
      }

      // Validate notes for non-JD, non-DSPD-1-on-1 services (skipped when
      // shift-note questions apply — they replace the freeform notes box)
      if (!this.isJDService && !this.isDSPDOneOnOneShift && !this.hasShiftNoteQuestions) {
        if (!this.shiftNotes.trim()) {
          this.$notifyError("Shift Notes are required");
          return;
        }

        if (this.shiftNotes.length < 20) {
          this.$notifyError("Shift Notes must be at least 20 characters");
          return;
        }
      }

      // Validate legacy Job Development fields (only when they are shown)
      if (this.showJdFields) {
        if (!this.jdActivity.trim()) {
          this.$notifyError("Job Development Activity is required");
          return;
        }
        if (this.jdActivity.length < 20) {
          this.$notifyError(
            "Job Development Activity must be at least 20 characters",
          );
          return;
        }
        if (!this.jdOutcome.trim()) {
          this.$notifyError("Outcome of Activity is required");
          return;
        }
        if (this.jdOutcome.length < 20) {
          this.$notifyError(
            "Outcome of Activity must be at least 20 characters",
          );
          return;
        }
        if (!this.jdNextSteps.trim()) {
          this.$notifyError("Next Steps Forward is required");
          return;
        }
        if (this.jdNextSteps.length < 20) {
          this.$notifyError(
            "Next Steps Forward must be at least 20 characters",
          );
          return;
        }
      }

      // Validate JC specific requirements
      if (this.isJCService) {
        if (this.selectedJCPrimaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one primary job coaching activity",
          );
          return;
        }
        if (this.selectedJCSecondaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one secondary job coaching activity",
          );
          return;
        }
        if (this.selectedJCPrimaryActivities.length > 3) {
          this.$notifyError("Please select no more than 3 primary activities");
          return;
        }
        if (this.selectedJCSecondaryActivities.length > 3) {
          this.$notifyError(
            "Please select no more than 3 secondary activities",
          );
          return;
        }
      }

      // Validate generic VR activities (each VR code with a list requires >=1).
      const vrActivityError = this.validateVrActivities({ requireMin: true });
      if (vrActivityError) {
        this.$notifyError(vrActivityError);
        return;
      }

      this.approving = true;
      try {
        // Get the shift date from scheduled_start to ensure we use the correct date
        // The shift.date field can sometimes be incorrect due to timezone conversions
        let shiftDate;
        const scheduledStart = this.shift?.scheduledStartTime || this.shift?.originalData?.scheduled_start || this.shift?.startTime || this.shift?.scheduled_start;

        if (scheduledStart && scheduledStart.includes('T')) {
          // Parse the UTC scheduled start time and convert to Mountain Time to get the correct date
          const utcDate = new Date(scheduledStart);
          const mountainTimeDate = new Date(utcDate.toLocaleString("en-US", { timeZone: "America/Denver" }));
          const year = mountainTimeDate.getFullYear();
          const month = String(mountainTimeDate.getMonth() + 1).padStart(2, '0');
          const day = String(mountainTimeDate.getDate()).padStart(2, '0');
          shiftDate = `${year}-${month}-${day}`;
        } else {
          // Fallback to shift.date if scheduled_start is not available
          shiftDate = this.shift?.date ? this.shift.date.split("T")[0] : localDateString(); // local today, not UTC
        }

        // Parse time strings (format: "HH:mm")
        const [startHours, startMinutes] = this.actualStartTime.split(":").map(Number);
        const [endHours, endMinutes] = this.actualEndTime.split(":").map(Number);

        // Parse date components and create Date objects in Mountain Time
        // Using the Date constructor with individual components creates a date in the browser's local timezone
        const [year, month, day] = shiftDate.split('-').map(Number);
        const clockInDateTime = new Date(year, month - 1, day, startHours, startMinutes, 0, 0);
        const clockOutDateTime = new Date(year, month - 1, day, endHours, endMinutes, 0, 0);

        // Handle overnight shifts
        if (clockOutDateTime.getTime() < clockInDateTime.getTime()) {
          clockOutDateTime.setDate(clockOutDateTime.getDate() + 1);
        }

        const clientId =
          this.shift.originalData?.clients?.[0]?.id ||
          this.shift.clients?.[0]?.id ||
          this.selectedClientId ||
          null;

        const caregiverId =
          this.shift.originalData?.caregivers?.[0]?.id ||
          this.shift.caregivers?.[0]?.id ||
          this.selectedCaregiverId ||
          null;

        const loginStore = useLoginStore();
        const leadId = loginStore.user?.id;

        const payload = {
          shiftId: this.shift.originalData?.id || this.shift.id,
          leadId: leadId,
          clientId: clientId,
          caregiverId: caregiverId,
          clockInTime: clockInDateTime.toISOString(),
          clockOutTime: clockOutDateTime.toISOString(),
          notes: this.isDSPDOneOnOneShift
            ? (this.showConcernsField ? (this.dspdConcerns || "").trim() : "")
            : this.shiftNotes.trim(),
          selectedServiceCode: this.hasMultipleServiceCodes ? this.selectedServiceCode : null,
          // Generic VR activities (LS/SED/SB and any other VR code), keyed by
          // service code.
          vrActivitiesSelected: this.buildVrActivitiesPayload(),
          // Job Development (CE/SE/SJBT/TWE) specific data
          isJDService: this.isJDService,
          jdActivity: this.showJdFields ? this.jdActivity : null,
          jdOutcome: this.showJdFields ? this.jdOutcome : null,
          jdNextSteps: this.showJdFields ? this.jdNextSteps : null,
          // JC specific data
          isJCService: this.isJCService,
          jcPrimaryActivities: this.isJCService
            ? this.selectedJCPrimaryActivities
                .map(
                  (actId) =>
                    this.jobCoachingActivities.find((act) => act.id === actId)
                      ?.name,
                )
                .filter(Boolean)
            : null,
          jcSecondaryActivities: this.isJCService
            ? this.selectedJCSecondaryActivities
                .map(
                  (actId) =>
                    this.jobCoachingActivities.find((act) => act.id === actId)
                      ?.name,
                )
                .filter(Boolean)
            : null,
          // DSPD 1-on-1 Q&A data
          isDSPDOneOnOneShift: this.isDSPDOneOnOneShift,
          questionAnswers: !this.isGroupShift
            ? Object.entries(this.dspdQuestionAnswers).map(([questionId, answer]) => ({
                question_id: Number(questionId),
                answer: answer,
              }))
            : null,
          goalAnswers: this.isDSPDOneOnOneShift
            ? Object.entries(this.dspdGoalAnswers).map(([goalId, answer]) => ({
                id_goals: Number(goalId),
                answer: answer,
              }))
            : null,
          dspdConcerns: this.showConcernsField ? this.dspdConcerns : null,
        };

        await shiftsService.approveMissedShift(payload);

        // Show different message for Non Service shifts (no authorization hours update)
        const isNonService = this.shift.payer === 'Non Service' || this.shift.originalData?.payer === 'Non Service';
        const successMessage = isNonService
          ? "Missed shift approved successfully!"
          : "Missed shift approved successfully! Authorization hours have been updated.";

        this.$notifySuccess(successMessage);
        this.isVisible = false;
        this.resetForm();
        this.$emit("shift-approved", {
          shiftId: this.shift.id,
        });
      } catch (error) {
        this.$notifyError(
          "Error approving missed shift: " +
            (error.response?.data?.message || error.message),
        );
      } finally {
        this.approving = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      let dateStr = dateString;
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }

      const [year, month, day] = dateStr.split('-');
      const dateObj = new Date(year, month - 1, day);

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      if (timeStr.includes('T')) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      if (date) {
        const dateOnly = date.split('T')[0];
        const localDateTime = `${dateOnly}T${timeStr}`;
        const localDate = new Date(localDateTime);

        if (!isNaN(localDate.getTime())) {
          return localDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      if (startTime && startTime.includes('T')) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatStatus(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers[0].name;
      }
      return shift.caregiver || "Unassigned";
    },

    getStatusClass(status) {
      if (!status) return "badge-secondary";

      const normalizedStatus = status.toLowerCase().replace(/\s+/g, "-");

      switch (normalizedStatus) {
        case "unassigned":
          return "badge-secondary";
        case "scheduled":
          return "badge-info";
        case "clocked-in":
        case "clocked in":
          return "badge-success";
        case "clocked-out":
        case "clocked out":
          return "badge-warning";
        case "approved":
          return "badge-primary";
        case "available":
          return "badge-success";
        case "missed":
          return "badge-missed";
        default:
          return "badge-info";
      }
    },

    formatServiceCode(shift) {
      if (!shift) return "Not specified";

      // Check for non-service codes first (for Non Service shifts)
      const nonServiceCodes = shift.non_service_codes || shift.nonServiceCodes || shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        const codes = nonServiceCodes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      return "Not specified";
    },
  },

  mounted() {
    if (this.isJCService) {
      this.fetchJobCoachingActivities();
    }
  },
};
</script>

<style scoped>
.approve-missed-shift-modal :deep(.dialog-content) {
  overflow-x: hidden;
}

.approve-missed-shift-content {
  width: 700px;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  box-sizing: border-box;
}

.approve-missed-shift-content .mb-3 {
  margin-bottom: 1.5rem;
}

.approve-missed-shift-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.approve-missed-shift-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.approve-missed-shift-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

:deep(.el-input__inner) {
  background-color: #656565 !important;
  border: 1px solid #bdbdbd !important;
  color: #ffffff !important;
  border-radius: 6px !important;
}

:deep(.el-input__inner):focus {
  border-color: #ffffff !important;
}

:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  border: 1px solid #bdbdbd !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-textarea__inner):focus {
  border-color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
}

.notes-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
}

.notes-section p {
  color: #e0e0e0;
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
}

.text-center {
  text-align: center;
}

.status-badge {
  display: inline-block !important;
  padding: 0.35rem 0.75rem !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  border-radius: 1rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.status-badge.badge-secondary {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.7),
    rgba(220, 53, 69, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-info {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-success {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  color: #000 !important;
}

.status-badge.badge-warning {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-primary {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  ) !important;
  color: #fff !important;
}

.activities-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
}

.activity-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.activity-tag {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: linear-gradient(
    135deg,
    rgba(52, 144, 220, 0.8),
    rgba(52, 144, 220, 0.6)
  );
  color: #fff;
  border-radius: 1.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(52, 144, 220, 0.3);
  text-align: center;
  line-height: 1.2;
}

.activity-tag.primary-activity {
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.8),
    rgba(40, 167, 69, 0.6)
  );
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.activity-tag.secondary-activity {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.8),
    rgba(255, 193, 7, 0.6)
  );
  color: #000;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.shift-notes-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.shift-notes-description ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.shift-notes-description li {
  margin-bottom: 0.25rem;
}

@media (max-width: 768px) {
  .approve-missed-shift-content {
    width: auto !important;
    padding: 0 1rem;
  }

  .activity-tags {
    gap: 0.25rem;
  }

  .activity-tag {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
}
</style>
